/**
 * Page Capture Hook
 *
 * Requests the extension to capture the active tab's page content (cleaned,
 * AI-optimised text). Follows the same postMessage pattern as useScreenCapture.
 *
 * Flow:
 * quickCapture()
 * → window.parent.postMessage({ type: 'REQUEST_PAGE_HTML' })
 * → sidepanel.ts forwards to background (CAPTURE_PAGE_HTML)
 * → background runs executeScript, cleans DOM, returns text
 * → sidepanel.ts sends PAGE_HTML_CAPTURED back to iframe
 * → this hook resolves the pending promise
 */

import { useEffect, useRef, useState } from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('web:page-capture');

export type PageCaptureData = {
  content: string;
  title: string;
  url: string;
  charCount: number;
  wasTruncated: boolean;
};

type PendingResolver = {
  resolve: (data: PageCaptureData) => void;
  reject: (err: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
};

export function usePageCapture() {
  const [isCapturing, setIsCapturing] = useState(false);
  const pending = useRef<PendingResolver | null>(null);

  const isAvailable = window.parent !== window;

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'PAGE_HTML_CAPTURED') {
        logger.info('✅ [PAGE-CAPTURE] Content received from extension');
        if (pending.current) {
          clearTimeout(pending.current.timeout);
          pending.current.resolve({
            content: event.data.content,
            title: event.data.title,
            url: event.data.url,
            charCount: event.data.charCount,
            wasTruncated: event.data.wasTruncated,
          });
          pending.current = null;
        }
        setIsCapturing(false);
      }

      if (event.data?.type === 'PAGE_HTML_CAPTURE_ERROR') {
        logger.error('❌ [PAGE-CAPTURE] Error from extension:', event.data.error);
        if (pending.current) {
          clearTimeout(pending.current.timeout);
          pending.current.reject(new Error(event.data.error ?? 'Page capture failed'));
          pending.current = null;
        }
        setIsCapturing(false);
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const captureCurrentPage = (): Promise<PageCaptureData> => {
    if (!isAvailable) {
      return Promise.reject(new Error('Page capture requires the browser extension'));
    }
    if (pending.current) {
      return Promise.reject(new Error('Capture already in progress'));
    }

    setIsCapturing(true);
    logger.info('📤 [PAGE-CAPTURE] Sending capture request to extension...');

    return new Promise<PageCaptureData>((resolve, reject) => {
      const timeout = setTimeout(() => {
        pending.current = null;
        setIsCapturing(false);
        reject(new Error('Page capture timed out after 15 seconds'));
      }, 15000);

      pending.current = { resolve, reject, timeout };

      window.parent.postMessage({ type: 'REQUEST_PAGE_HTML' }, '*');
    });
  };

  return { captureCurrentPage, isCapturing, isAvailable };
}
